/*
 * svnty-headstack.js v2 - THE HEADSTACK with the bar ABOVE the header (R142, 2026-09-23).
 * Port of a sibling store's sib-headstack.js (Part6-10) and the sibling's sib-announce.js rules, on SVNTY's selectors.
 * Classic deferred asset: no deps, no modules, no console, ASCII only. Guarded against double-init.
 *
 * THE SLAB: `.svnty-headstack` (theme.liquid) is the ONE sticky node holding the announcement bar and then the
 * header. Hiding the bar = translating the WHOLE slab up by the bar's height on a composited transform (CSS,
 * svnty-header.css SS8) - never a height, never the header alone; a toggle can never re-trigger itself.
 *
 * THE DECISION (Headroom's tolerance model): update() reads window.scrollY and nothing else. Directional
 * hysteresis: a toggle needs a SUSTAINED run past THRESHOLD measured from the point the direction last flipped.
 * THE TOP ZONE: at y <= SHOW_AT_TOP the bar is always whole. THE ENDS: rubber-band at the top or the bottom is
 * never a scroll. scrollend re-runs the decision once the scroll settles. THE BEAT is by distance: a full travel
 * takes BEAT, a short reversal proportionally less (floor BEAT_MIN).
 *
 * THE DOORS = THE LOCK: any layer that holds the page (drawer, search sheet, cart drawer, a modal) makes
 * svnty-header.js set `html.svnty-locked`. THE ONE LOCK (D6-17): svnty-header.js calls claim() while it takes the
 * lock; the first claim readies its parts (the curtain's place, the doors' slab height, the status colour) and
 * names `svnty-parked`, which lands on <html> in the same classList.add as the lock. Everything else is CSS from
 * that class (svnty-header.css 8b): the bar slides away on the door's beat and stops painting, the root paints
 * white, the curtain shows. The page's own state never changes under a door (svnty-bar-hidden and the root's
 * --svnty-headstack-h stay as the scroll left them), so nothing behind the door restyles or moves; only the slab,
 * where every door hangs, reads the parked height. The last release gives the bar back wherever the page is
 * (THE RETURN: parked becomes svnty-door-beat in one write). Under a claim no scroll moves the bar.
 *
 * THE STATUS BAR (the house law, ios-status-bar asset): iOS paints the strip under its status bar from a sample
 * of the static element at the page's top edge, refuses fixed and animating layers, falls back to the document
 * and root colour, and (26/27) ignores theme-color. So while a door is open: THE ROOT is painted white, THE
 * CURTAIN (a white box in the document flow at the held scroll spot, one node for the page's life, shown by CSS
 * while parked) sits under the slab, the parked bar is
 * `visibility: hidden` (CSS, on the beat), the slab's z-index steps by one on every hide/return and KNOCKS twice
 * after every claim/release (700 ms, 1600 ms) so Safari re-samples once the door has settled; theme-color
 * follows the bar for iOS 15-18. THE LIFT: the search sheet is absolute INSIDE the slab (top = the bar's height),
 * so it rides the slab's slide up with the bar and its edge lands at the viewport top mid-fade.
 *
 * WHAT IT WRITES: html.svnty-bar-hidden, html.svnty-at-top, html.svnty-headstack-stuck, html.svnty-door-beat (the
 * return's beat), the class it hands the lock (svnty-parked), the slab's .is-knock, the root vars --svnty-ann-h /
 * --svnty-headstack-h (VISIBLE slab height: full at rest, header-only when the bar is parked by the scroll, so every
 * sheet that hangs under the chrome lands right) / --svnty-slab-beat, the slab's own --svnty-headstack-h while a door
 * is open (the header alone: the doors hang in the slab), the meta theme-color, and one .svnty-curtain div.
 */
(function () {
  'use strict';
  if (window.__svntyHeadstack) return;
  var slab = document.querySelector('.svnty-headstack');
  var inner = slab && (slab.querySelector('.svnty-headstack__inner') || slab);   /* the node that slides (R142b) */
  var noop = function () {};
  if (!slab) { window.__svntyHeadstack = { hide: noop, show: noop, claim: function () { return []; }, release: noop, knock: noop, atTop: function () { return true; }, doors: function () { return 0; } }; return; }
  var root = document.documentElement;
  var body = document.body;
  var bar = slab.querySelector('.announcement-bar-section');
  var header = slab.querySelector('.section-header') || slab.querySelector('.header-wrapper');
  /* the stuck state is read from scrollY in update() (a sentinel above a sticky slab misreports at y = 0) */
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var HIDDEN = 'svnty-bar-hidden', TOP = 'svnty-at-top', STUCK = 'svnty-headstack-stuck', LOCK = 'svnty-locked', PARKED = 'svnty-parked';
  var BEAT_OUT = 0.14, BEAT_IN = 0.18, BEAT_MIN = 0.06, THRESHOLD = 6;   /* R142b: as responsive as the finger, out faster than back */
  var barH = 0, headerH = 0, above = 0, showAtTop = 0, maxY = 0, winH = window.innerHeight || 0;
  var lastY = window.scrollY || 0, refY = lastY, dir = 0, hidden = false, wasTop = null, wasStuck = null;
  var doors = 0, curtain = null, releaseTimer = null, beatTimer = null;
  var COARSE = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  var LAND = window.matchMedia && window.matchMedia('(max-width: 989px) and (max-height: 500px) and (orientation: landscape)');   /* the curtain is for touch Safari; a mouse has no status bar */
  var DOOR_BEAT = 'svnty-door-beat';   /* held on <html> for one beat after a release so the top zone's 0 s rule never snaps THE RETURN */

  /* ---------------------------------------------------------------- measurements (once per load/resize/section) */
  var slabSent = -1;   /* P24 (the bars, STICKY_SYSTEM_S.md §3): the visible slab height, as a getter and one event per change */
  function publish() {
    root.style.setProperty('--svnty-ann-h', barH + 'px');
    root.style.setProperty('--svnty-headstack-h', (hidden ? headerH : headerH + barH) + 'px');
    if ((hidden ? headerH : headerH + barH) !== slabSent) { slabSent = hidden ? headerH : headerH + barH; document.dispatchEvent(new CustomEvent('svnty:slab', { detail: { h: slabSent } })); }
    if (doors > 0) slab.style.setProperty('--svnty-headstack-h', headerH + 'px');
  }
  function measure() {
    /* the theme editor re-renders the section: re-find the bar and its colour every time (refutation #7) */
    var b = slab.querySelector('.announcement-bar-section');
    if (b !== bar) { bar = b; barColor = readBarColor(); if (bar && barObserver) barObserver.observe(bar); }
    barH = bar && !bar.hidden ? Math.round(bar.getBoundingClientRect().height) : 0;
    headerH = header ? Math.round(header.getBoundingClientRect().height) : Math.round(slab.getBoundingClientRect().height) - barH;
    above = 0;
    var sib = slab.previousElementSibling, pos;
    while (sib) {
      pos = getComputedStyle(sib).position;
      if (pos !== 'absolute' && pos !== 'fixed') above += sib.offsetHeight || 0;
      sib = sib.previousElementSibling;
    }
    showAtTop = above + barH;              /* the bar stays while the slab is still in the flow */
    winH = window.innerHeight || winH;
    maxY = Math.max(0, (root.scrollHeight || 0) - winH);
    lastY = refY = window.scrollY || 0;   /* THE RESTORE may have moved the page before we ran: re-base, never read the jump as a scroll */
    publish();
  }

  /* ---------------------------------------------------------------- the beat by distance */
  function setBeat(sec) { root.style.setProperty('--svnty-slab-beat', sec.toFixed(3) + 's'); }
  function currentShift() {
    var m = getComputedStyle(inner).transform, y = 0;
    if (m && m !== 'none') { var p = m.match(/matrix\(([^)]+)\)/); if (p) y = Math.abs(parseFloat(p[1].split(',')[5]) || 0); }
    return y;
  }
  /* D6-16 (the quick look door's open, R206): the slide's live shift is read only while it is still moving; a settled slab is
     at 0 or at the bar's height, so no door open or scroll forces a style recalculation of the whole page just to learn it */
  var settledAt = 0;
  function setHidden(next, force, beat) {
    if (next === hidden || !bar || !barH) return;
    if (!force && doors > 0) return;       /* no scroll moves the bar under an open door */
    var was = hidden;
    hidden = next;
    var sec;
    if (reduce) sec = 0;
    else if (beat) sec = beat;
    else {
      var y = performance.now() > settledAt ? (was ? barH : 0) : currentShift();
      var remaining = next ? (barH - y) : y;
      sec = Math.max(BEAT_MIN, (next ? BEAT_OUT : BEAT_IN) * (remaining / barH));
    }
    setBeat(sec);
    settledAt = performance.now() + sec * 1000 + 40;
    root.classList.toggle(HIDDEN, hidden);
    publish();
    paintStatusBar();
  }

  /* ---------------------------------------------------------------- THE STATUS BAR */
  var statusMeta = document.querySelector('meta[name="theme-color"]');
  function readBarColor() {
    if (!bar) return '';
    var els = [bar].concat(Array.prototype.slice.call(bar.querySelectorAll('*'))), i, c;
    for (i = 0; i < els.length && i < 12; i++) {
      c = getComputedStyle(els[i]).backgroundColor;
      if (c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent') return c;
    }
    return '';
  }
  var barColor = readBarColor();
  function paintStatusBar() { if (!statusMeta || !barColor) return; statusMeta.setAttribute('content', (hidden || doors > 0) ? '#ffffff' : barColor); }
  function knock() {
    slab.classList.add('is-knock');
    requestAnimationFrame(function () { requestAnimationFrame(function () { slab.classList.remove('is-knock'); }); });
  }
  function knocks() { setTimeout(knock, 700); setTimeout(knock, 1600); }
  /* THE CURTAIN (D6-17): one node for the page's life, `display: none` until html.svnty-parked shows it (svnty-header.css), so
     a door never inserts into <body> (every childList observer of the page would run on the open); its place is written
     on its own style before the lock's class lands, from the last scroll event and the body's own watch (no read) */
  function curtainOn() {
    if (!COARSE) return;
    if (!curtain) { curtain = document.createElement('div'); curtain.className = 'svnty-curtain'; curtain.setAttribute('aria-hidden', 'true'); }
    var y = Math.max(0, lastY), h = Math.min(winH + 300, Math.max(0, maxY + winH - y));
    curtain.style.top = y + 'px'; curtain.style.height = h + 'px';
    if (!curtain.parentNode) body.appendChild(curtain);
  }

  /* ---------------------------------------------------------------- THE DOORS */
  /* claim(): called by svnty-header.js while it takes the lock; returns the class to add with it (one write) */
  function claim() {
    if (releaseTimer) { clearTimeout(releaseTimer); releaseTimer = null; return []; }   /* a door handed to another door: keep the claim (refutation #4) */
    doors += 1;
    if (doors > 1) return [];
    curtainOn();
    /* the doors hang in the slab and read the parked slab (the header alone); the page keeps its own height, so nothing
       behind the door restyles or moves */
    slab.style.setProperty('--svnty-headstack-h', headerH + 'px');
    paintStatusBar();
    knocks();
    /* D8-9 (R240, 2026-09-26 04:2x, the desktop: "when the search page is opened at the top of the home the bar above
       the header disappears and leaves the strip empty above the hero; on the desktop keep the bar as is"): THE PARK is for
       touch Safari's status strip; a mouse keeps the bar where it stands. The doors still read the header-only height
       above: a door's box starts at the bar's foot (top = --svnty-ann-h), so its seat under the header is the header's own
       height in both cases. */
    return COARSE ? [PARKED] : [];
  }
  function doRelease() {
    releaseTimer = null;
    doors = Math.max(0, doors - 1);
    if (doors > 0) return;
    slab.style.removeProperty('--svnty-headstack-h');
    /* THE RETURN: parked gives way to the return's beat in one write (the top zone's 0 s rule never snaps it) */
    if (!root.classList.replace(PARKED, DOOR_BEAT)) root.classList.add(DOOR_BEAT);
    clearTimeout(beatTimer);
    beatTimer = setTimeout(function () { root.classList.remove(DOOR_BEAT); }, 420);
    paintStatusBar();
    knocks();
  }
  function release() {
    if (doors === 0 || releaseTimer) return;
    /* THE HANDOFF: the drawer's search field closes the drawer and opens the sheet a few frames later; the release
       waits 160 ms so the second door inherits the claim and the bar never bounces between them */
    releaseTimer = setTimeout(doRelease, 160);
  }
  /* ---------------------------------------------------------------- THE DECISION */
  function update() {
    var y = window.scrollY || 0;
    if (y < 0 || y > maxY + 1) { lastY = y; return; }            /* the ends: a spring-back is not a scroll */
    var atTop = y <= showAtTop;
    if (atTop !== wasTop) { wasTop = atTop; root.classList.toggle(TOP, atTop); }
    var stuck = y > above;
    if (stuck !== wasStuck) { wasStuck = stuck; root.classList.toggle(STUCK, stuck); }
    if (atTop) { setHidden(false); dir = 0; refY = y; lastY = y; return; }
    var d = y > lastY ? 1 : (y < lastY ? -1 : 0);
    if (d && d !== dir) { dir = d; refY = lastY; }
    if (dir === 1 && y - refY > THRESHOLD) setHidden(true);
    /* D6-12 (the landscape law, collection_guard CL9): on a rotated phone the bar never returns mid-page (the chrome must
       not eat the screen); it comes back at the top only (the atTop branch above) */
    if (dir === -1 && refY - y > THRESHOLD && !(LAND && LAND.matches)) setHidden(false);
    lastY = y;
  }
  /* focus landing inside the slab (keyboard, VoiceOver) reveals the bar */
  slab.addEventListener('focusin', function (e) {
    /* keyboard and VoiceOver only: a mouse focus would drop the slab 34 px under the pointer mid-click (refutation #3) */
    var kb = false;
    try { kb = e.target && e.target.matches(':focus-visible'); } catch (err) { kb = false; }
    if (kb && doors === 0) setHidden(false, true);
  });

  window.__svntyHeadstack = {
    hide: function () { setHidden(true, true); },
    show: function () { setHidden(false, true); },
    claim: claim, release: release, knock: knock, measure: measure, y: function () { return lastY; },
    atTop: function () { return (window.scrollY || 0) <= showAtTop; },
    doors: function () { return doors; },
    hidden: function () { return hidden; },
    h: function () { return hidden ? headerH : headerH + barH; }   /* P24: the visible slab height (the bars read it, never the CSS var) */
  };

  window.addEventListener('scroll', update, { passive: true });
  if ('onscrollend' in window) window.addEventListener('scrollend', update, { passive: true });
  window.addEventListener('resize', function () { measure(); update(); }, { passive: true });
  window.addEventListener('orientationchange', function () { setTimeout(function () { measure(); update(); }, 250); });
  if (window.visualViewport) window.visualViewport.addEventListener('resize', function () { lastY = window.scrollY || 0; }, { passive: true });
  window.addEventListener('load', measure);
  document.addEventListener('shopify:section:load', function () { setTimeout(measure, 0); });
  var barObserver = ('ResizeObserver' in window) ? new ResizeObserver(function () { measure(); }) : null;
  if (barObserver && bar) barObserver.observe(bar);
  /* the page grows after load (lazy rails, recommendations) and shrinks (filters): maxY follows the body (refutation #5) */
  if ('ResizeObserver' in window) new ResizeObserver(function () { maxY = Math.max(0, (root.scrollHeight || 0) - winH); }).observe(body);
  measure();
  /* the curtain is placed once, hidden, so no door ever inserts it; a lock taken before this script ran is claimed here
     (svnty-header.js claims and releases for every layer that holds the page) */
  curtainOn();
  if (root.classList.contains(LOCK)) { var boot = claim(); if (boot.length) root.classList.add.apply(root.classList, boot); }
  paintStatusBar();
  update();
})();
